import { useState, useCallback, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft, Check, SpinnerGap, Gauge, ChatCircleDots,
  TrendUp, TrendDown, Minus, ArrowSquareOut,
} from "@phosphor-icons/react";

import { ConfidenceLabel, ConfidenceMeter, ChipEditor, VoiceSection, InlineDiff, VoiceSummaryEditor, SamplePhrases } from "@/components/voice/voiceHelpers";
import { VoiceIntroStep } from "@/components/voice/VoiceIntroStep";
import { VoiceExamplesStep } from "@/components/voice/VoiceExamplesStep";
import { VoiceProcessingStep, PROCESSING_MESSAGES } from "@/components/voice/VoiceProcessingStep";

export default function VoiceWizardPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const businessId = searchParams.get("business_id") || user?.active_business_id || "";

  const [step, setStep] = useState("intro");
  const [examples, setExamples] = useState(["", "", ""]);
  const [guidance, setGuidance] = useState("");
  const [processing, setProcessing] = useState(false);
  const [processingMsg, setProcessingMsg] = useState(0);
  const [voiceProfile, setVoiceProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previousProfile, setPreviousProfile] = useState(null);
  const [showTest, setShowTest] = useState(false);
  const [testPrompt, setTestPrompt] = useState("");
  const [testSample, setTestSample] = useState("");
  const [testing, setTesting] = useState(false);

  const updateExample = (idx, val) => {
    setExamples((prev) => prev.map((e, i) => (i === idx ? val : e)));
  };

  const validExamples = examples.filter((e) => e.trim().length >= 100);
  const canAnalyze = validExamples.length >= 1;

  useEffect(() => {
    if (step !== "review" || !businessId) return;
    const fetchHistory = async () => {
      try {
        const resp = await api.get(`/brand/voice-history?business_id=${businessId}`);
        const hist = resp.data.history || [];
        if (hist.length > 0) setPreviousProfile(hist[hist.length - 1]);
        else if (resp.data.current?.extracted_at) setPreviousProfile(resp.data.current);
      } catch (err) { console.warn("Failed to load voice history:", err); }
    };
    fetchHistory();
  }, [step, api, businessId]);

  const handleAnalyze = useCallback(async () => {
    if (!canAnalyze) return;
    setStep("processing"); setProcessing(true); setProcessingMsg(0);
    const interval = setInterval(() => {
      setProcessingMsg((prev) => (prev < PROCESSING_MESSAGES.length - 1 ? prev + 1 : prev));
    }, 2200);
    try {
      const resp = await api.post("/brand/extract-voice", {
        business_id: businessId, examples: examples.filter((e) => e.trim()), guidance: guidance.trim() || null,
      });
      clearInterval(interval);
      if (resp.data.error) { toast.error(resp.data.error_message || "Voice extraction failed"); setStep("examples"); }
      else { setVoiceProfile(resp.data.voice_profile); setStep("review"); }
    } catch (err) {
      clearInterval(interval);
      toast.error(err.response?.data?.detail || "Voice extraction failed. Please try again."); setStep("examples");
    }
    setProcessing(false);
  }, [api, businessId, canAnalyze, examples, guidance]);

  const handleTestVoice = async () => {
    if (!testPrompt.trim()) return;
    setTesting(true); setTestSample("");
    try {
      const resp = await api.post("/brand/test-voice", { business_id: businessId, test_prompt: testPrompt });
      setTestSample(resp.data.sample_text || "No sample generated.");
    } catch (err) { toast.error(err.response?.data?.detail || "Voice test failed"); }
    setTesting(false);
  };

  const handleSave = async () => {
    if (!voiceProfile) return;
    setSaving(true);
    try {
      await api.post("/brand/save-voice-profile", { business_id: businessId, voice_profile: voiceProfile });
      toast.success("Voice profile saved!");
      navigate("/settings?tab=personalization");
    } catch { toast.error("Failed to save voice profile"); }
    setSaving(false);
  };

  const updateVP = (field, value) => setVoiceProfile((prev) => ({ ...prev, [field]: value }));

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <AnimatePresence mode="wait">
          {step === "intro" && <VoiceIntroStep onStart={() => setStep("examples")} onBack={() => navigate("/settings?tab=personalization")} />}
          {step === "examples" && (
            <VoiceExamplesStep examples={examples} onUpdateExample={updateExample} guidance={guidance} onGuidanceChange={setGuidance}
              canAnalyze={canAnalyze} onAnalyze={handleAnalyze} onBack={() => setStep("intro")} />
          )}
          {step === "processing" && <VoiceProcessingStep processingMsg={processingMsg} />}

          {step === "review" && voiceProfile && (
            <motion.div key="review" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="space-y-6" data-testid="voice-wizard-review">
              <button onClick={() => setStep("examples")} className="flex items-center gap-1.5 text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
                <ArrowLeft size={16} /> Back to examples
              </button>
              <div className="space-y-3">
                <h2 className="text-2xl font-bold text-[var(--text-primary)] font-['Cormorant_Garamond']" data-testid="review-heading">Your Brand Voice Profile</h2>
                <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">Confidence Score</span>
                    <Gauge size={18} className="text-[var(--text-muted)]" />
                  </div>
                  <ConfidenceMeter score={voiceProfile.confidence_score} />
                  <ConfidenceLabel score={voiceProfile.confidence_score} />
                  {voiceProfile.confidence_score < 5 && (
                    <button onClick={() => setStep("examples")} className="text-xs text-[#FF4400] hover:underline mt-1">Add more examples to improve accuracy</button>
                  )}
                </div>
              </div>
              <VoiceSummaryEditor voiceProfile={voiceProfile} onUpdate={updateVP} />
              <VoiceSection title="Tone Attributes"><ChipEditor items={voiceProfile.tone_attributes || []} onChange={(v) => updateVP("tone_attributes", v)} placeholder="Add tone attribute..." /></VoiceSection>
              <VoiceSection title="Vocabulary Style"><Textarea value={voiceProfile.vocabulary_style || ""} onChange={(e) => updateVP("vocabulary_style", e.target.value)} rows={2} className="bg-[var(--bg-base)] border-[var(--border-strong)] text-sm resize-y" data-testid="vocabulary-style-textarea" /></VoiceSection>
              <VoiceSection title="Sentence Structure"><Textarea value={voiceProfile.sentence_structure || ""} onChange={(e) => updateVP("sentence_structure", e.target.value)} rows={2} className="bg-[var(--bg-base)] border-[var(--border-strong)] text-sm resize-y" data-testid="sentence-structure-textarea" /></VoiceSection>
              <VoiceSection title="Personality Traits"><ChipEditor items={voiceProfile.personality_traits || []} onChange={(v) => updateVP("personality_traits", v)} placeholder="Add personality trait..." /></VoiceSection>
              <VoiceSection title="What to Avoid"><ChipEditor items={voiceProfile.what_to_avoid || []} onChange={(v) => updateVP("what_to_avoid", v)} color="red" placeholder="Add thing to avoid..." /></VoiceSection>
              <VoiceSection title="Sample Phrases"><SamplePhrases phrases={voiceProfile.sample_phrases} onUpdate={(v) => updateVP("sample_phrases", v)} /></VoiceSection>

              {previousProfile && previousProfile.extracted_at !== voiceProfile.extracted_at && (
                <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-5 space-y-4" data-testid="inline-evolution-panel">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">Voice Evolution</span>
                    <a href={`/settings/personalization/voice-history?business_id=${businessId}`} className="text-[10px] text-[#FF4400] hover:text-[#FF6633] font-medium flex items-center gap-1 transition-colors" data-testid="view-full-history-link">
                      View Full History <ArrowSquareOut size={10} />
                    </a>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 text-xs"><span className="text-[var(--text-muted)]">Previous:</span><span className="font-mono font-bold text-[var(--text-secondary)]">{previousProfile.confidence_score}/10</span></div>
                    <div className="text-[var(--text-muted)]">&rarr;</div>
                    <div className="flex items-center gap-2 text-xs"><span className="text-[var(--text-muted)]">New:</span><span className="font-mono font-bold text-[var(--text-primary)]">{voiceProfile.confidence_score}/10</span></div>
                    {voiceProfile.confidence_score > previousProfile.confidence_score && <TrendUp size={14} className="text-emerald-400" />}
                    {voiceProfile.confidence_score < previousProfile.confidence_score && <TrendDown size={14} className="text-red-400" />}
                    {voiceProfile.confidence_score === previousProfile.confidence_score && <Minus size={14} className="text-[var(--text-muted)]" />}
                  </div>
                  <InlineDiff label="Tone" prev={previousProfile.tone_attributes || []} curr={voiceProfile.tone_attributes || []} />
                  <InlineDiff label="Personality" prev={previousProfile.personality_traits || []} curr={voiceProfile.personality_traits || []} />
                  <InlineDiff label="Avoid" prev={previousProfile.what_to_avoid || []} curr={voiceProfile.what_to_avoid || []} />
                  {previousProfile.voice_summary !== voiceProfile.voice_summary && (
                    <div className="space-y-2 pt-2 border-t border-[var(--border)]">
                      <span className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">Summary Changed</span>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-[var(--bg-base)] border border-[var(--border)] rounded p-2.5">
                          <span className="text-[9px] text-red-400 uppercase tracking-wider block mb-1">Previous</span>
                          <p className="text-[11px] text-[var(--text-muted)] leading-relaxed italic">"{previousProfile.voice_summary}"</p>
                        </div>
                        <div className="bg-[var(--bg-base)] border border-[var(--border)] rounded p-2.5">
                          <span className="text-[9px] text-emerald-400 uppercase tracking-wider block mb-1">New</span>
                          <p className="text-[11px] text-[var(--text-secondary)] leading-relaxed italic">"{voiceProfile.voice_summary}"</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between gap-3 pt-6 border-t border-[var(--border)] flex-wrap" data-testid="review-action-bar">
                <button onClick={() => { setVoiceProfile(null); setStep("examples"); }} className="text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors" data-testid="start-over-btn">Start Over</button>
                <div className="flex items-center gap-3">
                  <Button variant="outline" onClick={() => setShowTest(true)} className="border-[var(--border)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]" data-testid="test-voice-btn">
                    <ChatCircleDots size={16} className="mr-1.5" /> Test Your Voice
                  </Button>
                  <Button onClick={handleSave} disabled={saving} className="bg-[#FF4400] hover:bg-[#FF6633] text-white px-6" data-testid="save-profile-btn">
                    {saving ? <SpinnerGap size={16} className="animate-spin mr-1.5" /> : <Check size={16} weight="bold" className="mr-1.5" />}
                    {saving ? "Saving..." : "Save Profile"}
                  </Button>
                </div>
              </div>

              <Dialog open={showTest} onOpenChange={setShowTest}>
                <DialogContent className="bg-[var(--bg-raised)] border-[var(--border-strong)] text-[var(--text-primary)] max-w-md" data-testid="test-voice-modal">
                  <DialogHeader><DialogTitle className="text-[var(--text-primary)]">Test Your Voice</DialogTitle></DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs text-[var(--text-muted)] mb-1 block">What should the AI write a quick sample about?</label>
                      <Input value={testPrompt} onChange={(e) => setTestPrompt(e.target.value)} placeholder='e.g. "Why consistency beats perfection in marketing"' className="bg-[var(--bg-base)] border-[var(--border-strong)] text-sm" data-testid="test-prompt-input" />
                    </div>
                    <Button onClick={handleTestVoice} disabled={testing || !testPrompt.trim()} className="w-full bg-[#FF4400] hover:bg-[#FF6633] text-white" data-testid="generate-sample-btn">
                      {testing ? <><SpinnerGap size={16} className="animate-spin mr-1.5" /> Generating...</> : "Generate Sample"}
                    </Button>
                    {testSample && (
                      <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} className="bg-[var(--bg-base)] border border-[var(--border)] rounded-lg p-4" data-testid="test-sample-output">
                        <p className="text-sm text-[var(--text-secondary)] leading-relaxed whitespace-pre-wrap">{testSample}</p>
                      </motion.div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button variant="ghost" onClick={() => setShowTest(false)} className="text-[var(--text-secondary)]">Back to Edit</Button>
                    {testSample && (
                      <Button onClick={() => { setShowTest(false); handleSave(); }} className="bg-[#FF4400] hover:bg-[#FF6633] text-white" data-testid="looks-good-save-btn">
                        Looks Good — Save Profile
                      </Button>
                    )}
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
